import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent,
    MatDialogModule, MatDialogRef, MatDialogTitle
} from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { CommonDataService } from '../../../../services/common-data.service';
import { CostReviewerDetails, CostReviewerPreferences, ReviewerTypeItem } from '../../../../models/dtos';
import { GetReviewDetailsService } from '../../../../services/api/get-review-details.service';
import { Subject, take, takeUntil } from 'rxjs';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { CONSTANTS } from '../../../../Constants/Constants';
import { IReviewerDialogData } from '../../../../models/common/IReviewerDialogData';
import { ICostReviewRequestDialogData } from '../../../../models/common/ICostReviewRequestDialogData';
import { IReviewerUpdateDialogResult } from '../../../../models/common/IReviewerUpdateDialogResult';
import { CommonMetaDataService } from '../../../../services/common-meta-data.service';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';

@Component({
    selector: 'app-cost-review-add-modify-reviewer',
    templateUrl: './cost-review-add-modify-reviewer.component.html',
    styleUrls: ['./cost-review-add-modify-reviewer.component.scss'],
    standalone: true,
    imports: [TranslatePipe,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        CommonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatButtonModule,
        MatDialogModule,
        CloseOnEscapeDirective
    ]
})
export class CostReviewAddModifyReviewerComponent implements OnInit, OnDestroy {

    isModifyMode: boolean;
    title: string;
    availableReviewers: CostReviewerDetails[] = [];
    reviewerRoles: ReviewerTypeItem[] = [];
    visibleReviewerRoles: ReviewerTypeItem[] = [];
    selectedReviewer: CostReviewerDetails | null = null;
    selectedRole: ReviewerTypeItem | null = null;
    verifiedUserId: string = CONSTANTS.EMPTY_STRING;
    isCostReviewRequest: boolean = false;
    reviewersPrefs: CostReviewerPreferences;
    existingReviewers: string[] = [];
    costReviewAddModifyReviewerForm: FormGroup<{
        name: FormControl<string>;
        email: FormControl<string>;
        role: FormControl<number>;
        available: FormControl<string>;
    }> = new FormGroup({
        name: new FormControl(CONSTANTS.EMPTY_STRING, [Validators.required]),
        email: new FormControl(CONSTANTS.EMPTY_STRING, [Validators.required, Validators.email]),
        role: new FormControl(1, [Validators.required]),
        available: new FormControl(CONSTANTS.EMPTY_STRING)
    });
    private isEmailVerified: boolean = false;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly dialogRef: MatDialogRef<CostReviewAddModifyReviewerComponent>,
        @Inject(MAT_DIALOG_DATA)
        private readonly data:{ 
            dialogData?: IReviewerDialogData,
            isCostReviewRequest: boolean 
        },
        private readonly commonDataService: CommonDataService,
        private readonly getReviewDetailsService: GetReviewDetailsService,
        private readonly notificationMessageService: NotificationMessageService,
        private readonly commonMetaDataService: CommonMetaDataService
    ) { }

    trackByReviewerId(index: number, reviewer: CostReviewerDetails): string {
        return reviewer.reviewUserId;
    }

    trackByRoleId(index: number, role: ReviewerTypeItem): number {
        return role.id;
    }

    get email(): string {
        return this.costReviewAddModifyReviewerForm.controls.email.value.trim();
    }

    get selectedReviewerRoleDescription(): string {
        return this.selectedRole?.description ?? CONSTANTS.EMPTY_STRING;
    }


    ngOnInit(): void {
        this.title = this.data.dialogData.title;
        this.isModifyMode = this.data.dialogData.isModifyMode;
        this.isCostReviewRequest = this.data.isCostReviewRequest;
        const preferenceData = this.commonMetaDataService.getMetaData();
        if (preferenceData) {
            this.reviewerRoles = preferenceData.costReviewerTypes;
            this.visibleReviewerRoles = this.reviewerRoles.filter(role => !role.isPolicyMandatedApprover);
            this.selectedRole = this.visibleReviewerRoles.length > CONSTANTS.ZERO ? this.visibleReviewerRoles[CONSTANTS.ZERO] : null;
        }
        
        if (this.isModifyMode) {
            this.availableReviewers = (this.data.dialogData?.policyMandatedApprovers ?? []).map(reviewer => {
                const reviewerDetails = new CostReviewerDetails();
                reviewerDetails.reviewerTypeID = reviewer.reviewerTypeId;
                reviewerDetails.reviewerType = reviewer.role;
                reviewerDetails.reviewerName = reviewer.reviewer;
                reviewerDetails.reviewerEmail = reviewer.email;
                reviewerDetails.reviewUserId = reviewer.userId;
                reviewerDetails.requiredBy = reviewer.requiredBy;
                reviewerDetails.isPolicyMandatedApprover = reviewer.isPolicyMandatedApprover ?? true;
                return reviewerDetails;
            });
            this.selectedRole = this.availableReviewers.length > CONSTANTS.ZERO ? {
                id: this.availableReviewers[CONSTANTS.ZERO].reviewerTypeID,
                description: this.availableReviewers[CONSTANTS.ZERO].reviewerType,
                isPolicyMandatedApprover: this.availableReviewers[CONSTANTS.ZERO].isPolicyMandatedApprover ?? false
            } : null;
        }
        else {
            this.reviewersPrefs = this.commonDataService.getPreferenceData().costReviewerPreferences;
            if (this.reviewersPrefs) {
                this.existingReviewers = this.reviewersPrefs.crReviewers?.map(reviewer => reviewer.reviewerEmail) ?? [];
                this.availableReviewers = this.reviewersPrefs.crAvailableReviewers ?? [];
            }
        }
        if (this.isCostReviewRequest && this.isModifyMode) {
            this.setInitialReviewerSelection();
        }
        this.costReviewAddModifyReviewerForm.get('email')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isEmailVerified = false;
        });
    }

    onVerifyClick() {
        const requestBody = { emailId: this.email };
        this.getReviewDetailsService.verifyCostReviewer(requestBody)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    if (response.isValidReviewer) {
                        this.verifiedUserId = response.bsetUserId;
                        this.isEmailVerified = true;
                    } else {
                        this.verifiedUserId = CONSTANTS.EMPTY_STRING;
                        this.isEmailVerified = false;
                        this.notificationMessageService.showNotificationMessageDialog(CONSTANTS.TYPE_ERROR, 'ERROR_TITLE', 'COST_REVIEW_ADD_MODIFY_UNAUTHORIZED_REVIEWER_ERROR', CONSTANTS.DEFAULT_DIALOG_WIDTH);
                    }
                },
                error: (error) => {
                    this.verifiedUserId = CONSTANTS.EMPTY_STRING;
                    this.isEmailVerified = false;
                    console.error('Error verifying reviewer:', error);
                }
            });
    }

    createDataForSelectedReviewer(roleLabel: string): ICostReviewRequestDialogData {
        const resolvedUserId = this.verifiedUserId ||
            this.reviewersPrefs.crAvailableReviewers?.find(r => r.reviewerEmail === this.email)?.reviewUserId ||
            CONSTANTS.EMPTY_STRING;
        return {
            role: roleLabel,
            reviewer: this.costReviewAddModifyReviewerForm.controls.name.value,
            email: this.email,
            requiredBy: null,
            remove: false,
            originalReviewerId: resolvedUserId,
            modifiedBy: this.commonDataService.getUserId(),
            reviewerTypeId: this.selectedRole ? this.selectedRole.id : 0,
            isPolicyMandatedApprover: this.selectedReviewer?.isPolicyMandatedApprover ?? false,
            userId: resolvedUserId
        }
    }   


    onAdd(): void {
        if (this.costReviewAddModifyReviewerForm.valid) {
            if (this.isEmailAlreadyAssigned()) {
                    this.notificationMessageService.showNotificationMessageDialog(
                    CONSTANTS.TYPE_ERROR,
                    'ERROR_TITLE',
                    'COST_REVIEWER_EXISTS',
                    CONSTANTS.DEFAULT_DIALOG_WIDTH
                );
                return;
            }
            const roleLabel = this.reviewerRoles.find(role => role.id === this.costReviewAddModifyReviewerForm.value.role)?.description || CONSTANTS.EMPTY_STRING;
            const data: ICostReviewRequestDialogData = this.createDataForSelectedReviewer(roleLabel);
            this.dialogRef.close(data);
        }
    }

    onAvailableReviewerChange(event: MatSelectChange): void {
        const selectedReviewerUserId = (event.value ?? CONSTANTS.EMPTY_STRING).toString();
        this.selectedReviewer = this.availableReviewers.find(
            reviewer => reviewer.reviewUserId === selectedReviewerUserId
        ) || null;
        this.updateFormFromSelectedReviewer();
    }

    onRoleChange(event: MatSelectChange): void {
        const selectedRoleId = event.value;
        this.selectedRole = this.reviewerRoles.find(role => role.id === selectedRoleId) || null;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onUpdateClick() {
        if (this.costReviewAddModifyReviewerForm.valid) {
            if (this.isEmailAlreadyAssigned()) {
                this.notificationMessageService.showNotificationMessageDialog(
                    CONSTANTS.TYPE_ERROR,
                    'ERROR_TITLE',
                    'COST_REVIEWER_EXISTS',
                    CONSTANTS.DEFAULT_DIALOG_WIDTH
                );
                return;
            }
            const selectedRole = this.reviewerRoles.find(role => role.id === this.costReviewAddModifyReviewerForm.value.role)?.description || CONSTANTS.EMPTY_STRING;
            const data = this.createDataForSelectedReviewer(selectedRole);
            const updateResult: IReviewerUpdateDialogResult = {
                type: 'updated',
                previousEmail: this.selectedReviewer?.reviewerEmail || CONSTANTS.EMPTY_STRING,
                data
            };
            this.dialogRef.close(updateResult);
        }
    }

    private isEmailAlreadyAssigned(): boolean {
        const email = this.email?.toLowerCase();
        let alreadyExists = false;
        if(this.isCostReviewRequest) {
            alreadyExists = this.data.dialogData.existingEmails.some(existingEmail => {
                const normalizedEmail = existingEmail.toLowerCase();
                return normalizedEmail === email;
            });
        }
        else{
            alreadyExists = this.existingReviewers.some(existingEmail => existingEmail.toLowerCase() === email)
                || this.availableReviewers.some(reviewer => reviewer.reviewerEmail?.toLowerCase() === email);
        }
        
        return alreadyExists;
    }

    get isVerifyDisabled(): boolean {
        const emailControl = this.costReviewAddModifyReviewerForm.controls.email;
        return this.isEmailVerified || emailControl.invalid;
    }

    get isAddDisabled(): boolean {
        return this.costReviewAddModifyReviewerForm.invalid || !this.isEmailVerified;
    }

    get isUpdateDisabled(): boolean {
        return this.costReviewAddModifyReviewerForm.invalid || !this.isEmailVerified;
    }

    private setInitialReviewerSelection(): void {
        const initialReviewer = this.availableReviewers[CONSTANTS.ZERO] ?? null;
        if (!initialReviewer) {
            return;
        }

        this.selectedReviewer = initialReviewer;
        this.costReviewAddModifyReviewerForm.controls.available.setValue(initialReviewer.reviewUserId || CONSTANTS.EMPTY_STRING);
        this.updateFormFromSelectedReviewer();
        this.isEmailVerified = false;
    }

    private updateFormFromSelectedReviewer(): void {
        if (!this.selectedReviewer) {
            this.isEmailVerified = false;
            return;
        }

        this.costReviewAddModifyReviewerForm.patchValue({
            name: this.selectedReviewer.reviewerName || CONSTANTS.EMPTY_STRING,
            email: this.selectedReviewer.reviewerEmail || CONSTANTS.EMPTY_STRING
        });

        if (this.isModifyMode) {
            this.selectedRole = this.reviewerRoles.find(role => role.id === this.selectedReviewer?.reviewerTypeID) || this.selectedRole;
            if (this.selectedRole) {
                this.costReviewAddModifyReviewerForm.controls.role.setValue(this.selectedRole.id);
            }
        }
        this.isEmailVerified = true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
